import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HeatmapResponse, FundamentalsResponse, AnalyticsSummaryResponse } from '@/api/types';
import { buildOuTree, type OuNode } from '@/components/common/OuTree/ouTree';
import { useOUItems } from '@/hooks/useOUs';
import { scoreStyle, fmtScore, getInitials, FUND_DEFAULT_COLORS } from '@/utils/analytics';

interface HeatmapTabProps {
  apiHeatmap?: HeatmapResponse;
  apiFundamentals?: FundamentalsResponse;
  apiSummary?: AnalyticsSummaryResponse;
}

/** Bucket sintético para filas de `apiHeatmap.rows` sin `ouId` (participante sin OU asignada). */
const SIN_OU_KEY = '__sin-ou__';

interface OuAggregate {
  /** Suma de score por fundamental, indexada igual que `apiHeatmap.fundamentalIds`. */
  sums: number[];
  /** Cantidad de scores numéricos sumados por fundamental (para promediar). */
  counts: number[];
  /** Participantes (filas) agregados en esta OU. */
  participantCount: number;
}

/** Fila de la tabla en el nivel de drill-down actual. */
interface HeatmapRow {
  id: string;
  n: string;
  p: number;
  /** true si esta fila tiene sub-OUs reales y por lo tanto se puede navegar hacia abajo. */
  drillable: boolean;
}

const TYPE_M = { label: 'OU', bg: '#FFF7ED', color: '#C2410C' };

const SCORE_LEGEND = [
  { range: '≤ 2.0', bg: '#fef2f2', text: '#991b1b', ring: '#fca5a5', labelKey: 'analytics.heatmap.legendCritical' },
  { range: '2.0–2.5', bg: '#fffbeb', text: '#92400e', ring: '#fcd34d', labelKey: 'analytics.heatmap.legendLow' },
  { range: '2.5–3.0', bg: '#fefce8', text: '#713f12', ring: '#fde047', labelKey: 'analytics.heatmap.legendMedium' },
  { range: '3.0–3.5', bg: '#ecfdf5', text: '#065f46', ring: '#6ee7b7', labelKey: 'analytics.heatmap.legendGood' },
  { range: '≥ 3.5',  bg: '#d1fae5', text: '#064e3b', ring: '#34d399', labelKey: 'analytics.heatmap.legendExcellent' },
];

const KPI_COLORS = ['#059669', '#F08521', '#2563eb', '#D97706'];

/** Recolecta recursivamente el id del nodo y de todos sus descendientes reales. */
function collectDescendantIds(node: OuNode, into: Set<string>): void {
  into.add(node.ou.id);
  node.children.forEach((child) => collectDescendantIds(child, into));
}

export function HeatmapTab({ apiHeatmap, apiFundamentals, apiSummary }: HeatmapTabProps) {
  const { t } = useTranslation();
  const { data: ouItems = [] } = useOUItems({ activeOnly: false });

  // Árbol completo (los 4 niveles reales) construido una sola vez a partir del catálogo plano
  // — sin ningún fetch adicional por nodo, a diferencia del fetch de red por OU raíz que tenía
  // esta pantalla antes.
  const ouForest = useMemo<OuNode[]>(() => buildOuTree(ouItems), [ouItems]);

  // Ruta de breadcrumb: nodo actualmente "abierto" en cada nivel. Vacío = raíz (toda la org).
  const [drillPath, setDrillPath] = useState<OuNode[]>([]);

  const currentLevelNodes = useMemo<OuNode[]>(() => {
    if (drillPath.length === 0) return ouForest;
    return drillPath[drillPath.length - 1]!.children;
  }, [drillPath, ouForest]);

  const funds = useMemo(() => {
    if (apiFundamentals && apiFundamentals.length > 0) {
      return apiFundamentals.map((f, i) => ({
        id: f.id,
        name: f.name,
        color: f.color ?? FUND_DEFAULT_COLORS[i % FUND_DEFAULT_COLORS.length]!,
      }));
    }
    return [];
  }, [apiFundamentals]);

  // Agrupa apiHeatmap.rows por ouId y promedia scores[i] (alineado con apiHeatmap.fundamentalIds)
  // dentro de cada OU real. Filas sin ouId (participante sin OU asignada) se agrupan bajo
  // SIN_OU_KEY en vez de excluirse del promedio, para no perder observaciones reales en silencio.
  const ouAggregates = useMemo(() => {
    const aggregates = new Map<string, OuAggregate>();
    if (!apiHeatmap) return aggregates;
    const fundamentalCount = apiHeatmap.fundamentalIds.length;
    for (const row of apiHeatmap.rows) {
      const ouKey = row.ouId && row.ouId.trim().length > 0 ? row.ouId : SIN_OU_KEY;
      let aggregate = aggregates.get(ouKey);
      if (!aggregate) {
        aggregate = {
          sums: new Array(fundamentalCount).fill(0),
          counts: new Array(fundamentalCount).fill(0),
          participantCount: 0,
        };
        aggregates.set(ouKey, aggregate);
      }
      aggregate.participantCount += 1;
      row.scores.forEach((score, idx) => {
        if (typeof score === 'number' && idx < fundamentalCount) {
          aggregate!.sums[idx] += score;
          aggregate!.counts[idx] += 1;
        }
      });
    }
    return aggregates;
  }, [apiHeatmap]);

  // Agregados del nivel de drill-down actual: cada fila visible representa un nodo real del
  // árbol de OUs, cuyo score/participantes es la suma de TODOS sus descendientes reales (no
  // solo los hijos directos) — así una fila sigue mostrando datos aunque los participantes
  // reales estén varios niveles más abajo. "Sin OU" solo aplica en la raíz: un participante sin
  // ouId no puede ubicarse en ningún nodo del árbol.
  const levelAggregates = useMemo(() => {
    const fundamentalCount = apiHeatmap?.fundamentalIds.length ?? 0;
    const result = new Map<string, OuAggregate>();
    for (const node of currentLevelNodes) {
      const descendantIds = new Set<string>();
      collectDescendantIds(node, descendantIds);
      const merged: OuAggregate = {
        sums: new Array(fundamentalCount).fill(0),
        counts: new Array(fundamentalCount).fill(0),
        participantCount: 0,
      };
      for (const ouId of descendantIds) {
        const agg = ouAggregates.get(ouId);
        if (!agg) continue;
        merged.participantCount += agg.participantCount;
        agg.sums.forEach((s, idx) => { merged.sums[idx] += s; });
        agg.counts.forEach((c, idx) => { merged.counts[idx] += c; });
      }
      result.set(node.ou.id, merged);
    }
    if (drillPath.length === 0 && ouAggregates.has(SIN_OU_KEY)) {
      result.set(SIN_OU_KEY, ouAggregates.get(SIN_OU_KEY)!);
    }
    return result;
  }, [currentLevelNodes, ouAggregates, apiHeatmap, drillPath]);

  // Matriz fila-actual × fundamental ya promediada, a partir de `levelAggregates`.
  const cellLookup = useMemo(() => {
    const lookup = new Map<string, Map<string, number>>();
    if (!apiHeatmap) return lookup;
    for (const [rowId, aggregate] of levelAggregates) {
      const fundMap = new Map<string, number>();
      apiHeatmap.fundamentalIds.forEach((fundId, idx) => {
        if (aggregate.counts[idx]! > 0) {
          fundMap.set(fundId, aggregate.sums[idx]! / aggregate.counts[idx]!);
        }
      });
      lookup.set(rowId, fundMap);
    }
    return lookup;
  }, [apiHeatmap, levelAggregates]);

  const rows = useMemo<HeatmapRow[]>(() => {
    const nodeRows = currentLevelNodes.map((node) => ({
      id: node.ou.id,
      n: node.ou.name,
      p: levelAggregates.get(node.ou.id)?.participantCount ?? 0,
      drillable: node.children.length > 0,
    }));
    if (drillPath.length === 0 && ouAggregates.has(SIN_OU_KEY)) {
      nodeRows.push({
        id: SIN_OU_KEY,
        n: t('analytics.heatmap.noOu'),
        p: ouAggregates.get(SIN_OU_KEY)!.participantCount,
        drillable: false,
      });
    }
    return nodeRows;
  }, [currentLevelNodes, levelAggregates, ouAggregates, drillPath, t]);

  const hasDrillableRow = rows.some((row) => row.drillable);
  const levelLabel = t('analytics.heatmap.unitOU');

  function handleRowClick(row: HeatmapRow): void {
    if (!row.drillable) return;
    const node = currentLevelNodes.find((candidate) => candidate.ou.id === row.id);
    if (node) setDrillPath((prev) => [...prev, node]);
  }

  function getScore(row: HeatmapRow, fundId: string): number | null {
    const score = cellLookup.get(row.id)?.get(fundId);
    return score === undefined ? null : score;
  }

  function getRowAvg(row: HeatmapRow): number | null {
    const rowMap = cellLookup.get(row.id);
    if (!rowMap || rowMap.size === 0) return null;
    const scores = [...rowMap.values()];
    return scores.reduce((s, v) => s + v, 0) / scores.length;
  }

  // Aproximación: `apiHeatmap` entrega un score ya agregado por participante, sin el conteo
  // de respuestas crudas que sí tenía el mock. Se reporta el número de participantes agregados
  // bajo la fila (nodo actual + todos sus descendientes reales).
  function getRowEvals(row: HeatmapRow): number {
    return levelAggregates.get(row.id)?.participantCount ?? 0;
  }

  // Mini KPIs — 100% desde apiSummary (mismo dato que consume ResumenTab); sin apiSummary, estado
  // vacío explícito (no fallback a mock).
  const miniKpis = [
    { label: t('analytics.kpi.avgScore'), value: apiSummary ? fmtScore(apiSummary.avgScore) : '—', color: KPI_COLORS[0] },
    { label: t('analytics.kpi.evaluations'), value: apiSummary?.totalEvaluations ?? 0, color: KPI_COLORS[1] },
    { label: t('analytics.kpi.evaluators'), value: apiSummary?.uniqueEvaluators ?? 0, color: KPI_COLORS[2] },
    { label: t('analytics.kpi.responseRate'), value: `${apiSummary?.responseRate ?? 0}%`, color: KPI_COLORS[3] },
  ];

  return (
    <>
      <div className="au-card">
        {/* Card header */}
        <div className="au-card-header">
          <div>
            <div className="au-card-title">
              <i className="fas fa-th" aria-hidden="true" />
              {t('analytics.heatmap.title')}
            </div>
            <div className="au-card-sub">{t('analytics.heatmap.subtitle')}</div>
          </div>
        </div>

        {/* Mini KPIs — label on top, colored value below */}
        <div className="hm-mini-kpis">
          {miniKpis.map((kpi) => (
            <div key={kpi.label} className="hm-mini-kpi">
              <div className="hm-mini-kpi-label">{kpi.label}</div>
              <div className="hm-mini-kpi-val" style={{ color: kpi.color }}>{kpi.value}</div>
            </div>
          ))}
        </div>

        {/* Breadcrumb — un crumb por nivel de drill-down real, basado en `buildOuTree`. */}
        <div className="au-breadcrumb" style={{ marginBottom: 12 }}>
          <button
            className={`au-bc-btn${drillPath.length === 0 ? ' active' : ''}`}
            onClick={() => setDrillPath([])}
          >
            {t('analytics.heatmap.allOrg')}
          </button>
          {drillPath.map((node, idx) => (
            <button
              key={node.ou.id}
              className={`au-bc-btn${idx === drillPath.length - 1 ? ' active' : ''}`}
              onClick={() => setDrillPath((prev) => prev.slice(0, idx + 1))}
            >
              {node.ou.name}
            </button>
          ))}
        </div>

        {/* Legend — above the table */}
        <div className="hm-legend-row">
          {SCORE_LEGEND.map((l) => (
            <span key={l.range} className="hm-legend-chip">
              <span className="hm-legend-dot" style={{ background: l.bg, borderColor: l.ring }} />
              {l.range} {t(l.labelKey)}
            </span>
          ))}
        </div>

        {/* Table */}
        <div className="hm-wrap">
          <table className="hm-table">
            <thead>
              <tr>
                <th className="hm-th-name">{levelLabel}</th>
                {funds.map((f) => (
                  <th key={f.id} className="hm-th-fund" style={{ color: f.color }}>
                    {f.name}
                  </th>
                ))}
                <th className="hm-th-fund">{t('analytics.kpi.avgScore')}</th>
                <th className="hm-th-fund">{t('analytics.kpi.evaluations')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowAvg = getRowAvg(row);
                const rowEvals = getRowEvals(row);
                const s = rowAvg !== null ? scoreStyle(rowAvg) : null;

                return (
                  <tr
                    key={row.id}
                    className={`hm-row${row.drillable ? ' drillable' : ''}`}
                    onClick={() => handleRowClick(row)}
                  >
                    <td className="hm-td-name">
                      <div className="hm-name-cell">
                        <div className="hm-icon">{getInitials(row.n)}</div>
                        <div>
                          <div className="hm-name-row">
                            <span className="hm-name">{row.n}</span>
                            <span
                              className="ona2-type"
                              style={{ background: TYPE_M.bg, color: TYPE_M.color }}
                            >
                              {TYPE_M.label}
                            </span>
                            {row.drillable && (
                              <i className="fas fa-chevron-right hm-drill-arrow" aria-hidden="true" />
                            )}
                          </div>
                          {row.p > 0 && (
                            <span className="hm-name-sub">
                              {row.p} {row.p === 1 ? t('analytics.heatmap.person') : t('analytics.heatmap.persons')}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    {funds.map((f) => {
                      const sc = getScore(row, f.id);
                      const cs = sc !== null ? scoreStyle(sc) : null;
                      return (
                        <td key={f.id} className="hm-td-score">
                          {cs !== null ? (
                            <span
                              className="hm-score-chip"
                              style={{ background: cs.bg, color: cs.text, borderColor: cs.ring }}
                            >
                              {fmtScore(sc!)}
                            </span>
                          ) : (
                            <span className="hm-no-data">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="hm-td-score">
                      {s !== null ? (
                        <span
                          className="hm-score-chip"
                          style={{ background: s.bg, color: s.text, borderColor: s.ring, fontWeight: 800 }}
                        >
                          {fmtScore(rowAvg!)}
                        </span>
                      ) : (
                        <span className="hm-no-data">—</span>
                      )}
                    </td>
                    <td className="hm-td-score hm-td-evals">
                      {rowEvals > 0 ? rowEvals : <span className="hm-no-data">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasDrillableRow && (
          <div className="hm-hint">{t('analytics.heatmap.hint')}</div>
        )}
      </div>
    </>
  );
}
